import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Users, Sparkles, Check, UserPlus } from "lucide-react";
import api from "../lib/api";

const KEY = "hifireels.followsuggest.lastShown";
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * FollowSuggestSheet — bottom-sheet that appears once per week on the reels
 * feed, suggesting up to 5 top creators to follow (drawn from the streamers
 * leaderboard, skipping people the user already follows).
 *
 * Dismissal is idempotent — persisted via `localStorage[hifireels.followsuggest.lastShown]`.
 */
export default function FollowSuggestSheet() {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [followed, setFollowed] = useState({}); // { userId: true }
  const [busy, setBusy] = useState({});

  useEffect(() => {
    // Timing gate — one week rolling window per browser
    try {
      const last = parseInt(localStorage.getItem(KEY) || "0", 10);
      if (last && Date.now() - last < ONE_WEEK_MS) return;
    } catch { /* localStorage disabled — sheet just doesn't show */ }

    let cancel = false;
    (async () => {
      try {
        const [board, followingRes, meRes] = await Promise.all([
          api.get("/leaderboard?type=streamers&period=30d&limit=15").catch(() =>
            api.get("/leaderboard?type=streamers&period=all&limit=15")),
          api.get("/me/following-ids").catch(() => ({ data: { ids: [] } })),
          api.get("/auth/me").catch(() => ({ data: null })),
        ]);
        if (cancel) return;
        const followedSet = new Set(followingRes.data?.ids || []);
        const myId = meRes.data?.id;
        const rows = (board.data?.items || [])
          .filter(r => r.user?.id && r.user.id !== myId && !followedSet.has(r.user.id))
          .slice(0, 5);
        if (rows.length >= 3) {
          setSuggestions(rows);
          // Wait 4s of viewing time before popping so it doesn't jump-scare
          setTimeout(() => !cancel && setOpen(true), 4000);
        }
      } catch { /* no-op */ }
    })();
    return () => { cancel = true; };
  }, []);

  const follow = async (u) => {
    if (!u?.id || busy[u.id]) return;
    setBusy(b => ({ ...b, [u.id]: true }));
    try {
      await api.post(`/users/${u.id}/follow`);
      setFollowed(f => ({ ...f, [u.id]: true }));
    } catch { /* ignore */ }
    finally { setBusy(b => ({ ...b, [u.id]: false })); }
  };

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(KEY, String(Date.now())); } catch { /* ignore */ }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="follow-suggest-sheet"
          className="fixed inset-0 z-[85] flex items-end justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={dismiss}/>
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 24, stiffness: 220 }}
            className="relative w-full md:max-w-md md:mb-6 md:rounded-3xl rounded-t-3xl bg-[#0B0F19] border border-white/15 max-h-[80vh] flex flex-col">
            <div className="flex items-start justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#A855F7] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-black"/>
                </div>
                <div>
                  <div className="font-outfit font-extrabold text-lg leading-none">People to follow</div>
                  <div className="text-[11px] text-white/55 mt-1">Top creators trending this month · picked for you</div>
                </div>
              </div>
              <button onClick={dismiss} data-testid="follow-suggest-close" className="p-1.5 rounded-full bg-white/8 hover:bg-white/12">
                <X className="w-4 h-4"/>
              </button>
            </div>
            <div className="p-3 space-y-2 overflow-y-auto">
              {suggestions.map(row => {
                const u = row.user; const done = followed[u.id];
                return (
                  <div key={u.id} data-testid={`fs-row-${u.id}`}
                    className="flex items-center gap-3 p-2.5 rounded-2xl bg-white/5 border border-white/10">
                    <img src={u.avatar} alt="" className="w-11 h-11 rounded-full object-cover border-2 border-[#00E5FF]/40"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{u.name || u.username}</div>
                      <div className="text-[11px] text-white/55 truncate">@{u.username} · {row.label || `${row.score} pts`}</div>
                    </div>
                    <button onClick={() => follow(u)} disabled={done || !!busy[u.id]}
                      data-testid={`fs-follow-${u.id}`}
                      className={`text-xs font-extrabold px-3 py-1.5 rounded-full flex items-center gap-1 disabled:opacity-70 active:scale-95 transition ${done ? "bg-white/10 text-white/70" : "bg-[#00E5FF] text-black"}`}>
                      {done ? <><Check className="w-3.5 h-3.5"/>Following</> : <><UserPlus className="w-3.5 h-3.5"/>Follow</>}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <button onClick={dismiss} data-testid="follow-suggest-later"
                className="flex-1 py-2 rounded-full bg-white/8 text-white/70 text-xs font-bold">Maybe later</button>
              <div className="text-[10px] text-white/40 flex items-center gap-1"><Users className="w-3 h-3"/>Shows once/week</div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
